import { Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { format, parseISO } from 'date-fns'
import { useEffect, useMemo } from 'react'

import { formatAmPm } from '@shared/dates'
import { i18WithDefault as t } from '@shared/locales'
import { Types } from 'service-api'
import { EarlyArrivalDetails } from './early_arrival_details'
import { LateDepartureDetails } from './late_departure_details'

export interface SurchargePolicyProps {
  order: Types.OrderClientDto
  bookingConfig: Types.BookingConfigsClientDto
}

export const formatTimeStringsToRange = (start?: string | null, end?: string | null) => {
  if (!start || !end) return ''
  return `${formatAmPm(start)} - ${formatAmPm(end)}`
}

const formatPolicyDate = (date?: string | null) => {
  if (!date) return ''
  try {
    return format(parseISO(date), 'EEEE, MMM d')
  } catch {
    return date
  }
}

export const SurchargePolicy = ({ order, bookingConfig }: SurchargePolicyProps) => {
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))

  const { startDate, endDate, startTime, endTime } = useMemo(() => {
    const invoice = order?.invoices?.find(({ items }) => {
      return items?.some(
        ({ locationServiceType, locationServiceTypeId }) =>
          (locationServiceType?.id || locationServiceTypeId) === order?.primaryServiceType?.id
      )
    })

    return invoice?.period?.[0] || {}
  }, [order])

  const isNotOvernight = !order?.primaryServiceType?.serviceType?.overnight
  const hasAnchor = window.location.hash === '#surcharge'

  useEffect(() => {
    if (hasAnchor) {
      const element = document.getElementById('surcharge')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const generateSurchargeDetails = () => {
    if (isNotOvernight && endDate) {
      return order?.invoices
        ?.filter(({ isInStay }) => !isInStay)
        ?.filter(({ period }) => Array.isArray(period) && period.length > 0)
        ?.map(({ items, period }, invoiceIdx) => {
          const selectedDate = period?.[0].startDate

          const daycareDateHeaderStr = `${formatPolicyDate(selectedDate)} ${formatTimeStringsToRange(
            period?.[0].startTime,
            period?.[0].endTime
          )}`
          const daycareHeader = items?.[0].displayName
            ? `${items?.[0].displayName} - ${daycareDateHeaderStr}`
            : daycareDateHeaderStr

          return (
            <LateDepartureDetails
              endDate={selectedDate}
              endTime={endTime}
              order={order}
              bookingConfig={bookingConfig}
              header={daycareHeader}
              key={invoiceIdx}
            />
          )
        })
    } else {
      return (
        <>
          {startDate && (
            <EarlyArrivalDetails
              startDate={startDate}
              startTime={startTime}
              order={order}
              bookingConfig={bookingConfig}
            />
          )}
          {endDate && (
            <LateDepartureDetails endDate={endDate} endTime={endTime} order={order} bookingConfig={bookingConfig} />
          )}
        </>
      )
    }
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        pb: 3,
        borderRadius: `${isBelowMD ? 0 : theme.shape.borderRadius}px`,
        borderColor: hasAnchor ? theme.palette.secondary.default : theme.palette.text.secondaryShade18,
        borderStyle: hasAnchor ? 'solid' : 'inherit',
      }}
      id="surcharge"
    >
      <Typography variant="h5" id="surcharge-title">
        {t('surchargePolicy.title')}
      </Typography>
      <Stack mt={2} gap={2}>
        {generateSurchargeDetails()}
      </Stack>
    </Paper>
  )
}
